import type { SearchBoxComponentType } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { pathnameWithoutLocale } from "@outschool/routes";
import React from "react";
import { useLocation } from "react-router";

import { useSearchByLearner } from "../../hooks/useSearchByLearner";
import { ExtendedSearchFiltersType } from "../../lib/useSearchFilters";

export default function ClassSearchForm({
  query,
  setQuery,
  onSubmit,
  placeholder: inputPlaceholder,
  onInputFocus = () => {},
  getInputProps = () => ({}),
  spellCheck = true,
  SearchBoxComponent,
  searchLabel,
  setSearchFilters,
  leftContent,
}: {
  query: string;
  setQuery: (query: string) => void;
  SearchBoxComponent: SearchBoxComponentType;
  onSubmit?: (query: string) => void;
  placeholder?: string;
  onInputFocus?: () => void;
  getInputProps?: () => object;
  spellCheck?: boolean;
  searchLabel?: string;
  useGlobalNavUi?: boolean;
  setSearchFilters: (filters: ExtendedSearchFiltersType) => void;
  leftContent?: React.ReactNode;
}) {
  const { t } = useTranslation("client\\components\\search\\ClassSearchForm");
  const { searchBarPlaceholder } = useSearchByLearner();
  const placeholder = inputPlaceholder ?? searchBarPlaceholder;

  const inputRef = React.createRef<HTMLInputElement>();

  const handleSubmit = (e: $TSFixMe) => {
    e.preventDefault();

    handleBlur();
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const handleBlur = () => {
    if (onSubmit) {
      onSubmit(query);
    } else {
      // Default behavior: redirect to listings page with query & user filters
      setSearchFilters({
        q: query && query.length ? query : undefined,
      });
    }
  };

  const handleChange = (e: $TSFixMe) => {
    setQuery(e.target.value || "");
  };

  const location = useLocation();

  const handleClear = () => {
    if (pathnameWithoutLocale(location.pathname) === "/search") {
      setSearchFilters({
        q: undefined,
      });
    }
    setQuery("");
  };

  return (
    <SearchBoxComponent
      onSubmit={handleSubmit}
      onClear={handleClear}
      inputProps={{
        ref: inputRef,
        placeholder,
        value: query || "",
        onFocus: onInputFocus,
        onBlur: handleBlur,
        onChange: handleChange,
        spellCheck,
        getInputProps,
      }}
      searchLabel={searchLabel || t("Search")}
      clearSearchAriaLabel={t("Clear")}
      leftContent={leftContent}
    />
  );
}
